// ============================================
// 🖼️ 이미지 압축 유틸리티
// ============================================

/**
 * 이미지 파일을 압축합니다
 * @param {File} file - 원본 이미지 파일
 * @param {Object} options - 압축 옵션
 * @returns {Promise<File>} 압축된 이미지 파일
 */
 export async function compressImage(file, options = {}) {
    const {
      maxWidth = 1200,
      maxHeight = 1200,
      quality = 0.8,
      outputFormat = 'image/jpeg'
    } = options;
  
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          // 캔버스 생성
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
  
          // 비율 유지하며 리사이즈
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
  
          canvas.width = width;
          canvas.height = height;
  
          // 이미지 그리기
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
  
          // Blob으로 변환
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('이미지 압축 실패'));
                return;
              }
  
              // File 객체 생성
              const compressedFile = new File(
                [blob],
                file.name,
                {
                  type: outputFormat,
                  lastModified: Date.now()
                }
              );
  
              resolve(compressedFile);
            },
            outputFormat,
            quality
          );
        };
  
        img.onerror = () => reject(new Error('이미지 로드 실패'));
        img.src = e.target.result;
      };
  
      reader.onerror = () => reject(new Error('파일 읽기 실패'));
      reader.readAsDataURL(file);
    });
  }
  
  /**
   * 썸네일 생성
   * @param {File} file - 원본 이미지 파일
   * @param {number} size - 썸네일 크기 (정사각형)
   * @returns {Promise<File>} 썸네일 파일
   */
  export async function createThumbnail(file, size = 200) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = size;
          canvas.height = size;
  
          const ctx = canvas.getContext('2d');
          
          // 중앙 크롭
          const minDimension = Math.min(img.width, img.height);
          const sx = (img.width - minDimension) / 2;
          const sy = (img.height - minDimension) / 2;
          
          ctx.drawImage(
            img,
            sx, sy, minDimension, minDimension,
            0, 0, size, size
          );
  
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error('썸네일 생성 실패'));
                return;
              }
  
              const thumbnailFile = new File(
                [blob],
                `thumb_${file.name}`,
                {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                }
              );
  
              resolve(thumbnailFile);
            },
            'image/jpeg',
            0.8
          );
        };
  
        img.onerror = () => reject(new Error('이미지 로드 실패'));
        img.src = e.target.result;
      };
  
      reader.onerror = () => reject(new Error('파일 읽기 실패'));
      reader.readAsDataURL(file);
    });
  }
  
  /**
   * 이미지 파일 검증
   * @param {File} file - 검증할 파일
   * @param {Object} options - 검증 옵션
   * @returns {Object} { valid: boolean, error: string }
   */
  export function validateImageFile(file, options = {}) {
    const {
      maxSize = 5 * 1024 * 1024, // 5MB
      allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
    } = options;
  
    // 파일 타입 검증
    if (!allowedTypes.includes(file.type)) {
      return {
        valid: false,
        error: '지원하지 않는 이미지 형식입니다. (jpg, png, webp만 가능)'
      };
    }
  
    // 파일 크기 검증
    if (file.size > maxSize) {
      const maxSizeMB = maxSize / 1024 / 1024;
      return {
        valid: false,
        error: `파일 크기는 ${maxSizeMB}MB 이하여야 합니다.`
      };
    }
  
    return { valid: true, error: null };
  }
  
  /**
   * 프로필 사진 처리 (정사각형 썸네일)
   * @param {File} file - 원본 파일
   * @returns {Promise<File>} 처리된 파일
   */
  export async function processProfilePhoto(file) {
    // 검증
    const validation = validateImageFile(file);
    if (!validation.valid) {
      throw new Error(validation.error);
    }
  
    // 압축 (정사각형, 400x400)
    return compressImage(file, {
      maxWidth: 400,
      maxHeight: 400,
      quality: 0.85,
      outputFormat: 'image/jpeg'
    });
  }
  
  /**
   * 계단 사진 처리
   * @param {File} file - 원본 파일
   * @returns {Promise<File>} 처리된 파일
   */
  export async function processStairPhoto(file) {
    // 검증
    const validation = validateImageFile(file, {
      maxSize: 10 * 1024 * 1024 // 10MB
    });
    if (!validation.valid) {
      throw new Error(validation.error);
    }
  
    // 압축 (1200x1200)
    return compressImage(file, {
      maxWidth: 1200,
      maxHeight: 1200,
      quality: 0.85,
      outputFormat: 'image/jpeg'
    });
  }
  
  /**
   * 여러 이미지 일괄 처리
   * @param {File[]} files - 파일 배열
   * @param {Function} processor - 처리 함수
   * @returns {Promise<File[]>} 처리된 파일 배열
   */
  export async function batchProcessImages(files, processor = compressImage) {
    const promises = files.map(file => processor(file));
    return Promise.all(promises);
  }
  
  /**
   * 이미지 크기 정보 가져오기
   * @param {File} file - 이미지 파일
   * @returns {Promise<Object>} { width, height, size }
   */
  export async function getImageInfo(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const img = new Image();
        
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height,
            size: file.size,
            sizeKB: (file.size / 1024).toFixed(2),
            sizeMB: (file.size / 1024 / 1024).toFixed(2)
          });
        };
  
        img.onerror = () => reject(new Error('이미지 로드 실패'));
        img.src = e.target.result;
      };
  
      reader.onerror = () => reject(new Error('파일 읽기 실패'));
      reader.readAsDataURL(file);
    });
  }
  
  export default {
    compressImage,
    createThumbnail,
    validateImageFile,
    processProfilePhoto,
    processStairPhoto,
    batchProcessImages,
    getImageInfo
  };